import { LODGE_EMAIL, LODGE_PHONE_SCHEMA } from "@/lib/contact-info";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,

  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteNav } from "../components/SiteNav";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SiteFooter } from "../components/SiteFooter";
import { Toaster } from "../components/ui/sonner";
import { initSentry, Sentry } from "../lib/sentry";
import { SentryBoundary } from "../components/SentryBoundary";
import { CartProvider } from "../lib/cart";
import { usePageViewTracking } from "../lib/page-views";

initSentry();

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    Sentry.captureException(error, {
      tags: { boundary: "tanstack_root_error_component" },
    });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

import { DEFAULT_OG_IMAGE, absoluteImageUrl } from "../lib/seo";

const OG_IMAGE_URL = DEFAULT_OG_IMAGE;
const ORG_LOGO_URL = absoluteImageUrl("/apple-touch-icon.png");

// Site-wide Organization schema. Appears on every route so crawlers can
// resolve brand identity, logo, and social profiles for knowledge-panel
// eligibility. LodgingBusiness / LocalBusiness schemas live on their own
// leaf routes (index, accommodations, reviews) to avoid duplicate primary
// entities across pages.
const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://coffmancovelodging.com/#organization",
  name: "Coffman Cove Lodging",
  url: "https://coffmancovelodging.com",
  logo: ORG_LOGO_URL,
  image: OG_IMAGE_URL,
  description:
    "Family-owned Alaska fishing lodge on Prince of Wales Island offering rooms, boat rentals, meals, and self-guided saltwater fishing since 1984.",
  telephone: LODGE_PHONE_SCHEMA,
  email: LODGE_EMAIL,
  foundingDate: "1984",
  address: {
    "@type": "PostalAddress",
    streetAddress: "306 Harbor Ave",
    addressLocality: "Coffman Cove",
    addressRegion: "AK",
    postalCode: "99918",
    addressCountry: "US",
  },
  sameAs: [
    "https://www.instagram.com/coffmancovelodging",
    "https://www.youtube.com/@coffmancovelodging",
  ],
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Coffman Cove Lodging — Alaska's Affordable Fishing" },
      {
        name: "description",
        content:
          "Family owned lodging, meals, and boat rentals on Prince of Wales Island, Alaska for over 40 years. Live fishing report, tides, and weather.",
      },
      { name: "author", content: "Coffman Cove Lodging" },
      { property: "og:site_name", content: "Coffman Cove Lodging" },
      { property: "og:title", content: "Coffman Cove Lodging — Alaska's Affordable Fishing" },
      {
        property: "og:description",
        content:
          "Lodging, meals & boat rentals on Prince of Wales Island. Family owned & operated for 40+ years.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE_URL },
      { property: "og:image:secure_url", content: OG_IMAGE_URL },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Coffman Cove Lodging — Alaska's Affordable Fishing, Since 1984" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Coffman Cove Lodging — Alaska's Affordable Fishing" },
      { name: "twitter:description", content: "Lodging, meals & boat rentals on Prince of Wales Island. Family owned & operated for 40+ years." },
      { name: "twitter:image", content: OG_IMAGE_URL },
      { name: "twitter:image:alt", content: "Coffman Cove Lodging — Alaska's Affordable Fishing, Since 1984" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(ORGANIZATION_JSON_LD),
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const routerState = useRouterState({ select: (s: { location: { pathname: string } }) => s.location.pathname });
  // Signage/kiosk routes render full-bleed with no site chrome.
  const isDisplay = routerState.startsWith("/display");
  usePageViewTracking(routerState);

  return (
    <QueryClientProvider client={queryClient}>
      <SentryBoundary name="app_root">
        <CartProvider>
          <div className="flex min-h-screen flex-col overflow-x-hidden">
            {!isDisplay && <SiteNav />}
            {!isDisplay && <Breadcrumbs />}
            <main className="flex-1">
              <SentryBoundary name="route_outlet">
                <Outlet />
              </SentryBoundary>
            </main>
            {!isDisplay && <SiteFooter />}
          </div>
        </CartProvider>

      </SentryBoundary>
      <Toaster richColors closeButton position="top-center" />
    </QueryClientProvider>
  );
}
